/**
 * Anonymous Profile Viewer
 *
 * Public lead-magnet page: lookup a company or a person profile without
 * signing up. Three request modes (handled server-side, the UI just adapts):
 *   - demo: clicked a curated chip (Stripe / Patrick Collison @ Stripe)
 *   - credits: logged-in user, charges credits, bypasses captcha + IP cap
 *   - free: anonymous, captcha + 1-per-IP-per-week + 100/day global
 *
 * Server data is in window.ANONYMOUS_VIEWER_BOOTSTRAP, parsed from the
 * <script type="application/json"> tag in the Jinja template.
 */

const DISCLAIMER =
  "Profile data is compiled from public sources across the web. We do not scrape LinkedIn or require a LinkedIn account.";

const BOOTSTRAP = (() => {
  try {
    const node = document.getElementById("anonymous-viewer-bootstrap");
    return node ? JSON.parse(node.textContent) : {};
  } catch (err) {
    console.error("Failed to parse viewer bootstrap:", err);
    return {};
  }
})();

const LOOKUP_URL = "/anonymous-profile-viewer/lookup";
const POLL_URL = "/anonymous-profile-viewer/poll";
const TWIMG_PROXY_URL = "/anonymous-profile-viewer/twimg";
const ENRICHMENT_POLL_INTERVAL_MS = 3000;
const ENRICHMENT_POLL_MAX_ATTEMPTS = 30;

const isTwimgUrl = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.hostname === "twimg.com" || parsed.hostname.endsWith(".twimg.com")
    );
  } catch {
    return false;
  }
};

const proxiedImageUrl = (url) => {
  if (!url || !isTwimgUrl(url)) return url;
  return `${TWIMG_PROXY_URL}?url=${encodeURIComponent(url)}`;
};

// The backend's `logo_url` points at our paid `/api/v1/company/logo` endpoint,
// which requires an API key — `<img>` tags can't authenticate, so it 401s and
// the avatar slot is empty. Mirror the dashboard explorer's fallback and hit
// Google's public s2 favicon endpoint instead. Caller passes a hostname like
// "stripe.com"; we accept null/empty and return null so the avatar slot keeps
// its placeholder.
const companyLogoUrl = (hostname) => {
  const host = (hostname || "").trim().toLowerCase();
  if (!host) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`;
};

// Pull the primary registered hostname out of a company profile. The backend
// returns both `hostnames[]` (deduped, primary first) and `websites[]`
// (raw URLs). Prefer the former; fall back to parsing a URL out of the latter.
const extractPrimaryHostname = (profile) => {
  const fromHostnames = (profile.hostnames || [])[0];
  if (fromHostnames) return fromHostnames;
  const fromWebsites = (profile.websites || [])[0];
  if (!fromWebsites) return null;
  try {
    return new URL(fromWebsites).hostname.replace(/^www\./, "");
  } catch {
    return fromWebsites
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("/")[0];
  }
};

// ─── Shared CSS helpers ──────────────────────────────────────────────────────
const INPUT_CLASS =
  "w-full px-3 py-2 border border-zinc-300 rounded-md focus:ring-2 " +
  "focus:ring-purple-500 focus:border-purple-500 outline-none";

const FIELD_LABEL_CLASS = "block text-sm font-medium text-zinc-700 mb-1";

// Label + input + optional helper text. Centralizes the styling that repeats
// 8x across the form.
function TextField({
  label,
  required,
  helper,
  inputProps,
}) {
  return (
    <div>
      <label className={FIELD_LABEL_CLASS}>
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input className={INPUT_CLASS} {...inputProps} />
      {helper && <p className="mt-1 text-xs text-zinc-500">{helper}</p>}
    </div>
  );
}

// Inline-flex icon + text pill, used in profile metadata rows.
function MetaPill({ icon, children }) {
  return (
    <span className="inline-flex items-center">
      {icon && <i className={`ph ${icon} mr-1`} />}
      {children}
    </span>
  );
}

// useReCaptcha hides the script-loaded-yet polling + grecaptcha.ready
// handshake + widget-id lifecycle inside one hook. Returns:
//   ref:        attach to the <div> where the widget should mount
//   getToken(): synchronously read the current response token (or "")
//   reset():    clear the widget so a new token can be obtained
function useReCaptcha(sitekey) {
  const ref = React.useRef(null);
  const widgetId = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !ref.current || widgetId.current != null) return;
      try {
        widgetId.current = window.grecaptcha.render(ref.current, { sitekey });
      } catch (err) {
        console.warn("Captcha render failed:", err);
      }
    };
    const waitForGrecaptcha = () => {
      if (cancelled) return;
      if (window.grecaptcha && typeof window.grecaptcha.ready === "function") {
        window.grecaptcha.ready(renderWidget);
        return;
      }
      setTimeout(waitForGrecaptcha, 100);
    };
    waitForGrecaptcha();
    return () => { cancelled = true; };
  }, [sitekey]);

  const getToken = () => {
    if (!window.grecaptcha || widgetId.current == null) return "";
    return window.grecaptcha.getResponse(widgetId.current) || "";
  };
  const reset = () => {
    if (!window.grecaptcha || widgetId.current == null) return;
    window.grecaptcha.reset(widgetId.current);
  };
  return { ref, getToken, reset };
}

// ─── Shared profile blocks ──────────────────────────────────────────────────

function CoverImage({ src }) {
  const resolvedSrc = proxiedImageUrl(src);
  if (resolvedSrc) {
    return (
      <div
        className="h-40 w-full rounded-t-lg bg-cover bg-center"
        style={{ backgroundImage: `url(${resolvedSrc})` }}
      />
    );
  }
  return (
    <div
      className="h-40 w-full rounded-t-lg"
      style={{
        background:
          "linear-gradient(120deg, #4c1d95 0%, #7e22ce 50%, #a855f7 100%)",
      }}
    />
  );
}

function ProfileHeader({ avatar, shape, title, subtitle, metadata }) {
  const avatarShape = shape === "round" ? "rounded-full" : "rounded-md";
  const resolvedAvatar = proxiedImageUrl(avatar);
  return (
    <div className="relative px-6 pb-5">
      <div
        className={`absolute -top-12 left-6 h-24 w-24 bg-white border-4 border-white shadow-md overflow-hidden ${avatarShape} flex items-center justify-center`}
      >
        {resolvedAvatar ? (
          <img
            src={resolvedAvatar}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div className="h-full w-full bg-zinc-200 flex items-center justify-center text-zinc-500 text-3xl font-semibold">
            {(title || "?").charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="pt-16">
        <h2 className="text-2xl font-semibold text-zinc-900">{title || "Unnamed"}</h2>
        {subtitle && <p className="mt-1 text-base text-zinc-600">{subtitle}</p>}
        {metadata && metadata.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm text-zinc-500">
            {metadata.map((m, i) => (
              <React.Fragment key={i}>{m}</React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileSection({ title, children }) {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white shadow-sm">
      <header className="px-6 pt-5 pb-2">
        <h3 className="text-lg font-semibold text-zinc-900">{title}</h3>
      </header>
      <div className="px-6 pb-5 text-zinc-700">{children}</div>
    </section>
  );
}

function TimelineEntry({ logo, title, subtitle, dateRange, description }) {
  const resolvedLogo = proxiedImageUrl(logo);
  return (
    <div className="flex gap-4 py-4 first:pt-0 last:pb-0 border-b border-zinc-100 last:border-b-0">
      <div className="flex-none w-12 h-12 rounded-md bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center text-zinc-400">
        {resolvedLogo ? (
          <img
            src={resolvedLogo}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <i className="ph ph-buildings text-xl" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-zinc-900">{title || "Untitled"}</p>
        {subtitle && <p className="text-sm text-zinc-600">{subtitle}</p>}
        {dateRange && (
          <p className="text-xs text-zinc-500 mt-0.5">{dateRange}</p>
        )}
        {description && (
          <p className="mt-2 text-sm text-zinc-700 whitespace-pre-wrap">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Profile view compositions ──────────────────────────────────────────────

function CompanyProfileView({ profile, creditsUsed, creditsRemaining }) {
  const hq =
    (profile.addresses || []).find((a) => a.is_primary) ||
    (profile.addresses || [])[0];
  const hqDisplay = hq
    ? [hq.city, hq.state, hq.country].filter(Boolean).join(", ")
    : null;
  const primaryHost = extractPrimaryHostname(profile);

  const metadata = [
    profile.industry && <MetaPill icon="ph-briefcase">{profile.industry}</MetaPill>,
    hqDisplay && <MetaPill icon="ph-map-pin">{hqDisplay}</MetaPill>,
    profile.founded_year && <MetaPill icon="ph-calendar">Founded {profile.founded_year}</MetaPill>,
    profile.employee_count && (
      <MetaPill icon="ph-users">{profile.employee_count.toLocaleString()} employees</MetaPill>
    ),
    primaryHost && (
      // shadow-none + inline boxShadow defends against any ancestor stylesheet
      // (Bootstrap defaults, browser extensions) that frames external links
      // with a drop-shadow.
      <a
        href={`https://${primaryHost}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-purple-700 hover:underline shadow-none focus:outline-none focus:ring-0"
        style={{ boxShadow: "none" }}
      >
        <i className="ph ph-globe mr-1" />
        {primaryHost}
      </a>
    ),
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      {creditsUsed != null && (
        <CreditsPill used={creditsUsed} remaining={creditsRemaining} />
      )}

      <article className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <CoverImage src={profile.cover_pic_url} />
        <ProfileHeader
          avatar={companyLogoUrl(primaryHost)}
          shape="square"
          title={profile.name}
          subtitle={profile.tagline}
          metadata={metadata}
        />
      </article>

      {profile.description && (
        <ProfileSection title="About">
          <p className="whitespace-pre-wrap leading-relaxed">{profile.description}</p>
        </ProfileSection>
      )}

      {profile.specialties && profile.specialties.length > 0 && (
        <ProfileSection title="Specialties">
          <div className="flex flex-wrap gap-2">
            {profile.specialties.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center px-3 py-1 rounded-full bg-zinc-100 text-sm text-zinc-700"
              >
                {s}
              </span>
            ))}
          </div>
        </ProfileSection>
      )}

      {profile.executives && profile.executives.length > 0 && (
        <ProfileSection title="Leadership">
          {profile.executives.map((e, i) => (
            <TimelineEntry
              key={i}
              title={e.name}
              subtitle={e.title}
              dateRange={e.role}
            />
          ))}
        </ProfileSection>
      )}

      {profile.addresses && profile.addresses.length > 0 && (
        <ProfileSection title="Locations">
          <ul className="space-y-2 text-sm">
            {profile.addresses.map((a, i) => (
              <li key={i} className="flex items-start gap-2">
                <i className="ph ph-map-pin text-zinc-400 mt-0.5" />
                <span>
                  {[a.line1, a.line2, a.city, a.state, a.postal_code, a.country]
                    .filter(Boolean)
                    .join(", ")}
                  {a.is_primary && (
                    <span className="ml-2 text-xs text-zinc-500">(primary)</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </ProfileSection>
      )}

      {profile.public_listing && (
        <ProfileSection title="Public listing">
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            {profile.public_listing.stock_symbol && (
              <>
                <dt className="text-zinc-500">Symbol</dt>
                <dd>{profile.public_listing.stock_symbol}</dd>
              </>
            )}
            {profile.public_listing.ipo_date && (
              <>
                <dt className="text-zinc-500">IPO date</dt>
                <dd>{profile.public_listing.ipo_date}</dd>
              </>
            )}
          </dl>
        </ProfileSection>
      )}

      <SocialLinks
        twitter={profile.twitter_url}
        facebook={profile.facebook_url}
        instagram={profile.instagram_url}
      />

      <DisclaimerFooter />
    </div>
  );
}

function PersonProfileView({ profile, creditsUsed, creditsRemaining, enriching }) {
  const currentWork = (profile.work_experience || []).find((w) => w.is_current);
  const headline = currentWork
    ? `${currentWork.role || ""}${currentWork.company_name ? ` at ${currentWork.company_name}` : ""}`.trim()
    : null;

  const metadata = [
    profile.location_display && (
      <MetaPill icon="ph-map-pin">{profile.location_display}</MetaPill>
    ),
    profile.follower_count && (
      <MetaPill icon="ph-users">
        {profile.follower_count.toLocaleString()} followers
      </MetaPill>
    ),
  ].filter(Boolean);

  return (
    <div className="space-y-4">
      {creditsUsed != null && (
        <CreditsPill used={creditsUsed} remaining={creditsRemaining} />
      )}

      {enriching && (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-medium text-zinc-600 animate-pulse">
          Updating profile…
        </div>
      )}

      <article className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <CoverImage src={null} />
        <ProfileHeader
          avatar={profile.profile_pic_url}
          shape="round"
          title={profile.full_name}
          subtitle={headline || null}
          metadata={metadata}
        />
      </article>

      {profile.bio && (
        <ProfileSection title="About">
          <p className="whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
        </ProfileSection>
      )}

      {profile.work_experience && profile.work_experience.length > 0 && (
        <ProfileSection title="Experience">
          {profile.work_experience.map((w, i) => (
            <TimelineEntry
              key={i}
              title={w.role}
              subtitle={w.company_name}
              dateRange={formatDateRange(w.start_date, w.end_date, w.is_current)}
              description={w.description}
            />
          ))}
        </ProfileSection>
      )}

      {profile.education && profile.education.length > 0 && (
        <ProfileSection title="Education">
          {profile.education.map((e, i) => (
            <TimelineEntry
              key={i}
              title={e.school}
              subtitle={e.major}
              dateRange={formatDateRange(e.start_date, e.end_date, false)}
            />
          ))}
        </ProfileSection>
      )}

      <SocialLinks
        twitter={profile.x_profile_url}
        website={profile.personal_website}
      />

      <DisclaimerFooter />
    </div>
  );
}

function formatDateRange(start, end, isCurrent) {
  if (!start && !end && !isCurrent) return null;
  const left = start || "?";
  const right = isCurrent ? "Present" : end || "?";
  return `${left} – ${right}`;
}

function SocialLinks({ twitter, facebook, instagram, website }) {
  const links = [
    twitter && { icon: "ph-x-logo", href: twitter, label: "X" },
    facebook && { icon: "ph-facebook-logo", href: facebook, label: "Facebook" },
    instagram && { icon: "ph-instagram-logo", href: instagram, label: "Instagram" },
    website && { icon: "ph-globe", href: website, label: "Website" },
  ].filter(Boolean);

  if (links.length === 0) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white shadow-sm px-6 py-4">
      <div className="flex flex-wrap gap-3">
        {links.map((l, i) => (
          <a
            key={i}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-zinc-200 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            <i className={`ph ${l.icon}`} />
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function CreditsPill({ used, remaining }) {
  return (
    <div className="flex justify-end">
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-sm text-purple-800">
        <i className="ph ph-coins" />
        {used} credit{used === 1 ? "" : "s"} used · {remaining} remaining
      </span>
    </div>
  );
}

function DisclaimerFooter() {
  return (
    <p className="text-xs text-zinc-500 text-center px-6 py-4">{DISCLAIMER}</p>
  );
}

// ─── Form-area components ───────────────────────────────────────────────────

function CreditsBanner({ balance, perCompany, perPerson, billingUrl }) {
  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-900">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          You're signed in. Lookups use{" "}
          <strong>{perPerson} credits (person)</strong> or{" "}
          <strong>{perCompany} credits (company)</strong> from your balance of{" "}
          <strong>{balance != null ? balance.toLocaleString() : "—"}</strong>{" "}
          credits.
        </div>
        {billingUrl && (
          <a href={billingUrl} className="font-medium text-purple-700 hover:underline whitespace-nowrap">
            Top up →
          </a>
        )}
      </div>
    </div>
  );
}

function SignupCTA({ cta }) {
  if (!cta) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-5 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[14rem]">
          <h4 className="font-semibold text-amber-900">{cta.headline}</h4>
          <p className="mt-1 text-sm text-amber-800">{cta.body}</p>
        </div>
        <a
          href={cta.url}
          className="inline-flex items-center px-4 py-2 rounded-md bg-amber-500 text-white font-medium hover:bg-amber-600 whitespace-nowrap"
        >
          Get {cta.trial_credits} free credits →
        </a>
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PERSON_INPUT_METHODS = [
  {
    key: "work_email",
    label: "Work email",
    icon: "ph-envelope-simple",
    fields: ["Work email"],
    // Validate returns an error message or null.
    validate(fields) {
      const we = fields.work_email.trim().toLowerCase();
      if (!we) return "Enter the person's work email.";
      if (!EMAIL_RE.test(we)) return "Enter a valid work email address.";
      return null;
    },
    toPayload(fields) {
      return { work_email: fields.work_email.trim().toLowerCase() };
    },
  },
  {
    key: "name_employer",
    label: "Name + employer",
    icon: "ph-user-circle",
    fields: ["First name", "Employer"],
    validate(fields) {
      if (!fields.first_name.trim() || !fields.employer_website.trim()) {
        return "Enter a first name and an employer website or name.";
      }
      return null;
    },
    toPayload(fields) {
      return {
        first_name: fields.first_name.trim(),
        middle_name: fields.middle_name.trim(),
        last_name: fields.last_name.trim(),
        employer_website: fields.employer_website.trim(),
      };
    },
  },
  {
    key: "employer_role",
    label: "Employer + role",
    icon: "ph-identification-badge",
    fields: ["Role", "Employer"],
    validate(fields) {
      if (!fields.employer_website.trim() || !fields.role.trim()) {
        return "Enter an employer website or name and a role.";
      }
      return null;
    },
    toPayload(fields) {
      return {
        employer_website: fields.employer_website.trim(),
        role: fields.role.trim(),
      };
    },
  },
];

function LookupForm({ bootstrap, busy, onSubmit, tab, onTabChange }) {
  const [companyQuery, setCompanyQuery] = React.useState("");
  const [personMethod, setPersonMethod] = React.useState("work_email");
  const [person, setPerson] = React.useState({
    work_email: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    employer_website: "",
    role: "",
  });
  const [formError, setFormError] = React.useState("");

  const captcha = useReCaptcha(bootstrap.recaptcha_site_key);

  const setPersonField = (key, value) => {
    setPerson((prev) => ({ ...prev, [key]: value }));
    if (formError) setFormError("");
  };

  const handleDemo = () => {
    const demo = bootstrap.demos && bootstrap.demos[tab];
    if (!demo) return;
    if (tab === "company") {
      onSubmit({ kind: "company", query: demo.website, demo: true });
    } else {
      // Demo person uses name + employer (Patrick Collison @ stripe.com).
      onSubmit({
        kind: "person",
        first_name: demo.first_name,
        last_name: demo.last_name,
        employer_website: demo.employer_website,
        demo: true,
      });
    }
  };

  const buildPayload = () => {
    if (tab === "company") {
      const q = companyQuery.trim();
      if (!q) {
        setFormError("Enter a company website or name.");
        return null;
      }
      return { kind: "company", query: q };
    }
    const method = PERSON_INPUT_METHODS.find((m) => m.key === personMethod);
    const err = method.validate(person);
    if (err) { setFormError(err); return null; }
    return { kind: "person", ...method.toPayload(person) };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;

    const token = captcha.getToken();
    if (!token) {
      setFormError("Please complete the captcha.");
      return;
    }
    payload["g-recaptcha-response"] = token;

    setFormError("");
    onSubmit(payload).finally(captcha.reset);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-zinc-200 bg-white shadow-sm p-6 space-y-5"
    >
      {/* Tabs */}
      <div className="flex p-1 bg-zinc-100 rounded-lg w-fit">
        {[
          { id: "company", label: "Company", icon: "ph-buildings" },
          { id: "person", label: "Person", icon: "ph-user" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              onTabChange(t.id);
              setFormError("");
            }}
            className={
              "px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 " +
              (tab === t.id
                ? "bg-white text-zinc-900 shadow-sm"
                : "text-zinc-600 hover:text-zinc-900")
            }
          >
            <i className={`ph ${t.icon}`} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "company" ? (
        <TextField
          label="Company website or name"
          helper="Paste a website URL or type the company name."
          inputProps={{
            type: "text",
            value: companyQuery,
            onChange: (e) => {
              setCompanyQuery(e.target.value);
              if (formError) setFormError("");
            },
            placeholder: "stripe.com  ·  Stripe  ·  https://stripe.com",
          }}
        />
      ) : (
        <PersonInputs
          method={personMethod}
          onMethodChange={(m) => {
            setPersonMethod(m);
            setFormError("");
          }}
          fields={person}
          onFieldChange={setPersonField}
        />
      )}

      {formError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {formError}
        </div>
      )}

      {/* Demo chip */}
      {bootstrap.demos && bootstrap.demos[tab] && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-500">Free demo:</span>
          <button
            type="button"
            onClick={handleDemo}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 border border-amber-200 text-amber-900 hover:bg-amber-200 disabled:opacity-50"
          >
            <i className="ph-fill ph-sparkle" />
            {bootstrap.demos[tab].label}
          </button>
        </div>
      )}

      <div ref={captcha.ref} />

      <button
        type="submit"
        disabled={busy}
        className="w-full px-4 py-2.5 rounded-md bg-purple-700 text-white font-medium hover:bg-purple-800 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {busy ? "Looking up…" : "View profile"}
      </button>
    </form>
  );
}

function PersonInputs({ method, onMethodChange, fields, onFieldChange }) {
  const showWorkEmail = method === "work_email";
  const showNameFields = method === "name_employer";
  const showEmployer = method === "name_employer" || method === "employer_role";
  const showRole = method === "employer_role";

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="text-xs font-semibold text-zinc-700">Choose one input method</div>
          <div className="text-[11px] text-zinc-400">Work email is selected by default</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {PERSON_INPUT_METHODS.map((m) => {
            const active = method === m.key;
            return (
              <button
                key={m.key}
                type="button"
                aria-pressed={active}
                onClick={() => onMethodChange(m.key)}
                className={
                  "min-h-[82px] rounded-md border px-3 py-2 text-left transition-colors " +
                  (active
                    ? "border-purple-300 bg-purple-50 text-purple-900 ring-1 ring-purple-200"
                    : "border-zinc-200 bg-white text-zinc-700 hover:border-purple-200 hover:bg-purple-50/50")
                }
              >
                <div className="mb-1.5 flex items-center gap-1.5">
                  <i className={`ph ${m.icon} ${active ? "text-purple-700" : "text-zinc-400"}`} />
                  <span className="text-xs font-semibold">{m.label}</span>
                  {active && <i className="ph-bold ph-check-circle ml-auto text-purple-600" />}
                </div>
                <div className="flex flex-wrap gap-1">
                  {m.fields.map((f) => (
                    <span
                      key={f}
                      className={
                        "inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium " +
                        (active
                          ? "border-purple-200 bg-white text-purple-800"
                          : "border-zinc-200 bg-zinc-50 text-zinc-500")
                      }
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {showWorkEmail && (
        <TextField
          label="Work email"
          inputProps={{
            type: "email",
            autoFocus: true,
            value: fields.work_email,
            onChange: (e) => onFieldChange("work_email", e.target.value),
            placeholder: "patrick@stripe.com",
          }}
        />
      )}

      {showNameFields && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <TextField
            label="First name"
            inputProps={{
              autoFocus: true,
              value: fields.first_name,
              onChange: (e) => onFieldChange("first_name", e.target.value),
              placeholder: "Patrick",
            }}
          />
          <TextField
            label="Middle name"
            inputProps={{
              value: fields.middle_name,
              onChange: (e) => onFieldChange("middle_name", e.target.value),
            }}
          />
          <TextField
            label="Last name"
            inputProps={{
              value: fields.last_name,
              onChange: (e) => onFieldChange("last_name", e.target.value),
              placeholder: "Collison",
            }}
          />
        </div>
      )}

      {showRole && (
        <TextField
          label="Role"
          inputProps={{
            autoFocus: true,
            value: fields.role,
            onChange: (e) => onFieldChange("role", e.target.value),
            placeholder: "CEO",
          }}
        />
      )}

      {showEmployer && (
        <TextField
          label="Employer website or name"
          inputProps={{
            value: fields.employer_website,
            onChange: (e) => onFieldChange("employer_website", e.target.value),
            placeholder: "stripe.com  ·  Stripe",
          }}
        />
      )}
    </div>
  );
}

// ─── Example preview (rendered until the user runs a lookup) ───────────────

const EXAMPLE_COMPANY = {
  name: "Stripe",
  tagline: "Payments infrastructure for the internet",
  description:
    "Stripe is a software platform for starting and running internet businesses. Millions of businesses rely on Stripe's tools to accept online payments, send payouts, and manage their businesses online.",
  industry: "Internet Software & Services",
  founded_year: 2010,
  employee_count: 8000,
  websites: ["https://stripe.com"],
  hostnames: ["stripe.com"],
  cover_pic_url: null,
  logo_url: null,
  specialties: [
    "Payments", "Billing", "Connect", "Radar", "Issuing", "Atlas", "Climate",
  ],
  addresses: [
    {
      line1: "510 Townsend Street",
      city: "San Francisco",
      state: "California",
      country: "United States",
      country_code: "US",
      postal_code: "94103",
      is_primary: true,
    },
    {
      line1: "1 Grand Canal Street Lower",
      city: "Dublin",
      country: "Ireland",
      country_code: "IE",
      is_primary: false,
    },
  ],
  executives: [
    { name: "Patrick Collison", title: "CEO & Co-founder", role: "CEO" },
    { name: "John Collison", title: "President & Co-founder", role: "PRESIDENT" },
    { name: "Steffan Tomlinson", title: "Chief Financial Officer", role: "CFO" },
  ],
  twitter_url: "https://twitter.com/stripe",
  facebook_url: null,
  instagram_url: null,
  public_listing: null,
};

const EXAMPLE_PERSON = {
  full_name: "Patrick Collison",
  profile_pic_url: null,
  bio:
    "Co-founder and CEO of Stripe. Building economic infrastructure for the internet — payments, billing, and financial tools used by millions of businesses around the world.",
  location_display: "San Francisco, California, United States",
  follower_count: 482000,
  x_handle: "patrickc",
  x_profile_url: "https://twitter.com/patrickc",
  personal_website: "https://patrickcollison.com",
  work_experience: [
    {
      role: "CEO & Co-founder",
      company_name: "Stripe",
      start_date: "2010",
      end_date: null,
      is_current: true,
      description:
        "Building economic infrastructure for the internet. Stripe processes hundreds of billions of dollars in payments annually for millions of companies worldwide.",
    },
    {
      role: "CEO & Co-founder",
      company_name: "Auctomatic",
      start_date: "2007",
      end_date: "2008",
      is_current: false,
      description:
        "eBay seller tools, acquired by Live Current Media in 2008.",
    },
  ],
  education: [
    {
      school: "Massachusetts Institute of Technology",
      major: "Mathematics",
      start_date: "2007",
      end_date: "2009",
    },
  ],
};

function ExamplePreview({ tab }) {
  const isCompany = tab === "company";
  return (
    <section className="relative">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-medium text-zinc-600">
          <i className="ph-fill ph-eye" />
          Sample {isCompany ? "company" : "person"} preview
        </span>
        <p className="text-xs text-zinc-500">
          Here's what a result looks like. Try the demo or run a real lookup
          above to see live data.
        </p>
      </div>
      <div className="opacity-95 pointer-events-none select-none">
        {isCompany ? (
          <CompanyProfileView
            profile={EXAMPLE_COMPANY}
            creditsUsed={null}
            creditsRemaining={null}
          />
        ) : (
          <PersonProfileView
            profile={EXAMPLE_PERSON}
            creditsUsed={null}
            creditsRemaining={null}
          />
        )}
      </div>
    </section>
  );
}

// ─── App root ───────────────────────────────────────────────────────────────

function App() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [errorCta, setErrorCta] = React.useState(null);
  const [tab, setTab] = React.useState("company");

  const handleSubmit = async (payload) => {
    setBusy(true);
    setResult(null);
    setError(null);
    setErrorCta(null);
    try {
      const res = await fetch(LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || `Lookup failed (HTTP ${res.status}).`);
        setErrorCta(data.cta || null);
        return;
      }
      setResult(data);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // While the backend's detailed enrichment is still running, poll the
  // token-gated endpoint and swap in the complete profile when it lands.
  const pollToken = result?.poll_token;
  React.useEffect(() => {
    if (!pollToken || !result || result.enrichment_status !== "pending") return undefined;

    let cancelled = false;
    let attempts = 0;
    let timeoutId = null;

    const finish = () => {
      setResult((prev) => (prev ? { ...prev, enrichment_status: "complete" } : prev));
    };

    const poll = () => {
      timeoutId = setTimeout(async () => {
        attempts += 1;
        try {
          const res = await fetch(`${POLL_URL}?token=${encodeURIComponent(pollToken)}`);
          if (cancelled) return;
          if (!res.ok) {
            finish();
            return;
          }
          const data = await res.json();
          if (cancelled) return;
          setResult((prev) => (
            prev
              ? { ...prev, profile: data.profile, enrichment_status: data.enrichment_status }
              : prev
          ));
          if (data.enrichment_status === "pending" && attempts < ENRICHMENT_POLL_MAX_ATTEMPTS) {
            poll();
          } else if (data.enrichment_status === "pending") {
            finish();
          }
        } catch (err) {
          if (!cancelled) finish();
        }
      }, ENRICHMENT_POLL_INTERVAL_MS);
    };
    poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [pollToken]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl sm:text-4xl font-semibold text-zinc-900 leading-tight">
          View Professional Profiles without logging in
        </h1>
        <p className="text-zinc-600 max-w-2xl">
          Look up company and people profiles from a single page. No LinkedIn
          account required.
        </p>
      </header>

      {BOOTSTRAP.signed_in && (
        <CreditsBanner
          balance={BOOTSTRAP.credit_balance}
          perCompany={BOOTSTRAP.credits_per_company}
          perPerson={BOOTSTRAP.credits_per_person}
          billingUrl={BOOTSTRAP.billing_url}
        />
      )}

      <LookupForm
        bootstrap={BOOTSTRAP}
        busy={busy}
        onSubmit={handleSubmit}
        tab={tab}
        onTabChange={setTab}
      />

      <p className="text-xs text-zinc-500 text-center">{DISCLAIMER}</p>

      {error && (
        <div className="space-y-3">
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
          {errorCta && <SignupCTA cta={errorCta} />}
        </div>
      )}

      {result && result.kind === "company" && (
        <CompanyProfileView
          profile={result.profile}
          creditsUsed={result.credits_used}
          creditsRemaining={result.credits_remaining}
        />
      )}
      {result && result.kind === "person" && (
        <PersonProfileView
          profile={result.profile}
          creditsUsed={result.credits_used}
          creditsRemaining={result.credits_remaining}
          enriching={result.enrichment_status === "pending"}
        />
      )}

      {!result && !error && !busy && <ExamplePreview tab={tab} />}
    </div>
  );
}

const _viewerMount = document.getElementById("anonymous-profile-viewer-app");
if (_viewerMount) {
  ReactDOM.createRoot(_viewerMount).render(<App />);
}
